import React, { useState, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import * as sass from 'sass';
import less from 'less';

const CssBeautifierPage = () => {
  const [cssInput, setCssInput] = useState('');
  const [cssOutput, setCssOutput] = useState('');
  const [indentSize, setIndentSize] = useState('2');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [autoUpdate, setAutoUpdate] = useState(true);
  const fileInputRef = useRef(null);

  // Sample CSS for demonstration
  const sampleCSS = `.container{margin:0 auto;padding:20px;max-width:1200px}.header{background-color:#333;color:#fff;padding:10px 20px}.button{background:#007bff;color:#fff;border:none;padding:10px 20px;border-radius:4px;cursor:pointer}.button:hover{background:#0056b3}@media(max-width:768px){.container{padding:10px}.header{font-size:14px}}`;

  // CSS Minification
  const minifyCSS = (css) => {
    let result = css;
    result = result.replace(/\/\*[\s\S]*?\*\//g, '');
    result = result.replace(/\/\/.*$/gm, '');
    result = result.replace(/\s+/g, ' ');
    result = result.replace(/\s*([{}:;,>+~\[\]()])\s*/g, '$1');
    result = result.replace(/;}/g, '}');
    result = result.replace(/(\s|:)0+\.(\d+)/g, '$1.$2');
    result = result.replace(/#([0-9a-fA-F])\1([0-9a-fA-F])\2([0-9a-fA-F])\3/g, '#$1$2$3');
    return result.trim();
  };

  // CSS Beautification
  const beautifyCSS = (css) => {
    const indent = ' '.repeat(parseInt(indentSize));
    let result = css;

    // FIRST: Protect pseudo-elements by replacing :: patterns with a placeholder
    const pseudoElements = [];
    let placeholderIndex = 0;

    // Find and store all ::pseudo-element patterns (including vendor prefixes)
    result = result.replace(/::[a-z-]+/gi, (match) => {
      const placeholder = `__PSEUDO_${placeholderIndex}__`;
      pseudoElements.push({ placeholder, original: match });
      placeholderIndex++;
      return placeholder;
    });

    // Remove all extra whitespace and normalize
    result = result.replace(/\s+/g, ' ').trim();

    // Normalize spacing
    result = result.replace(/\s*\{\s*/g, ' {');
    result = result.replace(/\s*\}\s*/g, '}');
    result = result.replace(/\s*;\s*/g, ';');
    // Only add space after : if it's NOT part of a placeholder and NOT before another :
    result = result.replace(/:\s*(?!:)/g, ': ');

    // Restore pseudo-elements
    pseudoElements.forEach(({ placeholder, original }) => {
      result = result.replace(placeholder, original);
    });

    // Add newlines after opening braces
    result = result.replace(/\{/g, ' {\n');

    // Add newlines after semicolons
    result = result.replace(/;/g, ';\n');

    // Add newlines before and after closing braces
    result = result.replace(/\}/g, '\n}\n\n');

    // Handle selector commas
    result = result.replace(/,(?![^(]*\))/g, ',\n');

    // Split and indent properly
    const lines = result.split('\n');
    const formatted = [];
    let indentLevel = 0;

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();

      // Skip empty lines during processing
      if (!line) {
        // Only add empty line between rules
        if (formatted.length > 0 && !formatted[formatted.length - 1].trim().endsWith('}')) {
          continue;
        }
        if (i < lines.length - 1 && lines[i + 1].trim().startsWith('}')) {
          continue;
        }
        formatted.push('');
        continue;
      }

      // Decrease indent for closing brace
      if (line === '}') {
        indentLevel = Math.max(0, indentLevel - 1);
        formatted.push(indent.repeat(indentLevel) + line);
        continue;
      }

      // Add line with current indentation
      formatted.push(indent.repeat(indentLevel) + line);

      // Increase indent after opening brace
      if (line.endsWith('{')) {
        indentLevel++;
      }
    }

    // Join and clean up excessive blank lines
    result = formatted.join('\n');
    result = result.replace(/\n{3,}/g, '\n\n');

    return result.trim();
  };

  // Compile SASS/SCSS to CSS
  const compileSass = async (input, syntax = 'scss') => {
    try {
      const result = sass.compileString(input, {
        syntax: syntax,
      });
      return result.css;
    } catch (e) {
      throw new Error(`SASS compilation error: ${e.message}`);
    }
  };

  // Compile LESS to CSS
  const compileLess = async (input) => {
    try {
      const result = await less.render(input);
      return result.css;
    } catch (e) {
      throw new Error(`LESS compilation error: ${e.message}`);
    }
  };

  // Detect file type from content
  const detectFileType = (content) => {
    if (content.includes('$') && /\$[\w-]+\s*:/.test(content)) {
      return 'scss';
    }
    if (content.includes('@') && /@[\w-]+\s*:/.test(content) && !content.includes('@media') && !content.includes('@keyframes')) {
      return 'less';
    }
    // eslint-disable-next-line no-useless-escape
    if (/\.\w+\s*\{[^}]*\.\w+\s*\{/.test(content)) {
      return 'scss';
    }
    return 'css';
  };

  // Format CSS (auto-detects type and beautifies)
  const formatCSS = async () => {
    try {
      setError('');

      if (!cssInput.trim()) {
        setError('Please enter CSS, SASS, or LESS code');
        return;
      }

      const detectedType = detectFileType(cssInput);
      let compiledCSS = cssInput;

      // Compile SASS/SCSS/LESS to CSS first
      if (detectedType === 'scss' || detectedType === 'sass') {
        compiledCSS = await compileSass(cssInput, detectedType === 'sass' ? 'indented' : 'scss');
      } else if (detectedType === 'less') {
        compiledCSS = await compileLess(cssInput);
      }

      // Beautify the CSS
      const output = beautifyCSS(compiledCSS);
      setCssOutput(output);
    } catch (e) {
      setError('Error processing CSS: ' + e.message);
      setCssOutput('');
    }
  };

  // Minify CSS
  const minifyCSS_handler = async () => {
    try {
      setError('');

      if (!cssInput.trim()) {
        setError('Please enter CSS, SASS, or LESS code');
        return;
      }

      const detectedType = detectFileType(cssInput);
      let compiledCSS = cssInput;

      // Compile SASS/SCSS/LESS to CSS first
      if (detectedType === 'scss' || detectedType === 'sass') {
        compiledCSS = await compileSass(cssInput, detectedType === 'sass' ? 'indented' : 'scss');
      } else if (detectedType === 'less') {
        compiledCSS = await compileLess(cssInput);
      }

      // Minify the CSS
      const output = minifyCSS(compiledCSS);
      setCssOutput(output);
    } catch (e) {
      setError('Error processing CSS: ' + e.message);
      setCssOutput('');
    }
  };

  // Auto-process on input change
  React.useEffect(() => {
    if (autoUpdate && cssInput.trim()) {
      const timer = setTimeout(() => {
        formatCSS();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cssInput, indentSize, autoUpdate]);

  // Clear all
  const clearAll = () => {
    setCssInput('');
    setCssOutput('');
    setError('');
    setCopySuccess(false);
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const text = cssOutput || cssInput;
    if (!text) return;

    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // Download CSS
  const downloadCSS = () => {
    const content = cssOutput || cssInput;
    if (!content) return;

    const blob = new Blob([content], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted.css';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // File upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCssInput(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  // Load sample
  const loadSample = () => {
    setCssInput(sampleCSS);
    setCssOutput('');
  };

  const buttonStyle = {
    padding: '10px 20px',
    margin: '5px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  };

  return (
    <>
      <SEO
        title="CSS Compressor Online - Minify & Beautify CSS, SASS, LESS Free Tool"
        description="Free online CSS compressor and beautifier tool. Minify CSS, SASS, LESS files to reduce size or beautify messy stylesheets instantly. Supports SCSS, preprocessors, nesting, variables, and mixins. No installation required."
        keywords={["CSS compressor online", "CSS minifier", "CSS beautifier", "minify CSS online", "beautify CSS", "SASS beautifier", "LESS beautifier", "SCSS formatter", "compress CSS file", "CSS optimizer", "reduce CSS size", "CSS minify tool", "online CSS formatter", "CSS code minifier", "SASS minifier", "LESS minifier", "CSS preprocessor formatter", "online SCSS beautifier"]}
        canonicalUrl="https://yourjsontools.com/css-compressor"
      />

      <div style={{ width: '85%', margin: '0 auto', padding: '20px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '10px', color: '#333' }}>
            CSS Compressor - Minify & Beautify
          </h1>
          <p style={{ fontSize: '16px', color: '#666' }}>
            Compress, minify and beautify CSS, SASS, and LESS code online - Free tool
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {/* Input & Output Areas - Side by Side */}
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          {/* Input Area */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <label style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                Paste your CSS/SASS/LESS code here
              </label>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {cssInput.length} characters
              </span>
            </div>
            <textarea
              value={cssInput}
              onChange={(e) => setCssInput(e.target.value)}
              placeholder="Paste your CSS, SASS, SCSS or LESS code here..."
              style={{
                width: '100%',
                height: '500px',
                padding: '15px',
                fontSize: '14px',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                border: '1px solid #ddd',
                borderRadius: '4px',
                resize: 'none',
                boxSizing: 'border-box',
                lineHeight: '1.6'
              }}
            />
          </div>

          {/* Middle Control Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: '180px'
          }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              accept=".css,.scss,.sass,.less"
            />

            <button
              onClick={() => fileInputRef.current.click()}
              style={{
                ...buttonStyle,
                width: '100%',
                margin: '0'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              Upload File
            </button>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#fff',
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              width: '100%',
              justifyContent: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}>
              <input
                type="checkbox"
                checked={autoUpdate}
                onChange={(e) => setAutoUpdate(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span>Auto Update</span>
            </label>

            <select
              value={indentSize}
              onChange={(e) => setIndentSize(e.target.value)}
              style={{
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '14px',
                cursor: 'pointer',
                width: '100%',
                backgroundColor: '#fff',
                boxSizing: 'border-box'
              }}
            >
              <option value="2">2 Spaces</option>
              <option value="4">4 Spaces</option>
              <option value="8">8 Spaces</option>
            </select>

            <button
              onClick={formatCSS}
              style={{
                ...buttonStyle,
                backgroundColor: '#28a745',
                width: '100%',
                margin: '0'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
            >
              Format
            </button>

            <button
              onClick={minifyCSS_handler}
              style={{
                ...buttonStyle,
                width: '100%',
                margin: '0'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              Minify
            </button>

            <button
              onClick={loadSample}
              style={{
                ...buttonStyle,
                width: '100%',
                margin: '0'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              Load Sample
            </button>

            <button
              onClick={clearAll}
              style={{
                ...buttonStyle,
                width: '100%',
                margin: '0'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              Clear
            </button>

            <button
              onClick={copyToClipboard}
              style={{
                ...buttonStyle,
                width: '100%',
                margin: '0'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              {copySuccess ? '✓ Copied!' : 'Copy'}
            </button>

            <button
              onClick={downloadCSS}
              style={{
                ...buttonStyle,
                width: '100%',
                margin: '0'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              Download
            </button>
          </div>

          {/* Output Area */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <label style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                Output
              </label>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {cssOutput.length} characters
                {cssInput.length > 0 && cssOutput.length > 0 && cssOutput.length < cssInput.length && (
                  <span style={{ marginLeft: '10px', color: '#28a745', fontWeight: '600' }}>
                    ({((1 - cssOutput.length / cssInput.length) * 100).toFixed(1)}% smaller)
                  </span>
                )}
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '500px',
              padding: '15px',
              fontSize: '14px',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box',
              lineHeight: '1.6',
              backgroundColor: '#282c34',
              color: '#abb2bf',
              overflowY: 'auto',
              whiteSpace: 'pre',
              position: 'relative'
            }}>
              {cssOutput ? (
                <pre style={{ margin: 0, color: '#abb2bf', whiteSpace: 'pre' }}>
                  {cssOutput.split('\n').map((line, i) => {
                    // Colorize selectors (before {)
                    if (line.trim().endsWith('{')) {
                      const selector = line.replace('{', '').trim();
                      return (
                        <div key={i}>
                          <span style={{ color: '#e06c75' }}>{selector}</span>
                          <span style={{ color: '#abb2bf' }}> {'{'}</span>
                        </div>
                      );
                    }
                    // Colorize closing braces
                    else if (line.trim() === '}') {
                      return (
                        <div key={i}>
                          <span style={{ color: '#abb2bf' }}>{line}</span>
                        </div>
                      );
                    }
                    // Colorize properties (property: value;)
                    else if (line.includes(':')) {
                      const [property, ...valueParts] = line.split(':');
                      const value = valueParts.join(':');
                      return (
                        <div key={i}>
                          <span style={{ color: '#61afef' }}>{property}</span>
                          <span style={{ color: '#abb2bf' }}>:</span>
                          <span style={{ color: '#98c379' }}>{value}</span>
                        </div>
                      );
                    }
                    // Default for other lines
                    else {
                      return <div key={i}>{line || '\u00A0'}</div>;
                    }
                  })}
                </pre>
              ) : (
                <span style={{ color: '#5c6370', fontStyle: 'italic' }}>
                  Formatted CSS will appear here...
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div style={{ marginTop: '40px', padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#333' }}>
            About CSS Compressor & Minifier
          </h2>

          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '10px', color: '#333' }}>
              What is CSS Compression?
            </h3>
            <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#666' }}>
              CSS compression (minification) removes unnecessary characters (whitespace, comments) without changing functionality. This reduces file size and improves website loading speed by 30-60%.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '10px', color: '#333' }}>
              What is CSS Beautification?
            </h3>
            <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#666' }}>
              CSS beautification formats minified or messy CSS code by adding proper indentation, line breaks, and spacing. This makes the code more readable and easier to maintain. Our tool automatically detects and supports CSS, SASS, SCSS, and LESS.
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: '18px', marginBottom: '10px', color: '#333' }}>
              Key Features
            </h3>
            <ul style={{ fontSize: '15px', lineHeight: '1.8', color: '#666', paddingLeft: '20px' }}>
              <li>✓ Auto-detects CSS, SASS, SCSS, and LESS</li>
              <li>✓ Minify CSS to reduce file size by 30-60%</li>
              <li>✓ Beautify CSS with customizable indentation (2, 4, or 8 spaces)</li>
              <li>✓ Syntax highlighted output for better readability</li>
              <li>✓ Auto-update mode for instant formatting</li>
              <li>✓ Upload CSS files or paste code directly</li>
              <li>✓ Download processed CSS files</li>
              <li>✓ Copy to clipboard with one click</li>
              <li>✓ Load sample CSS for testing</li>
              <li>✓ 100% free - no registration required</li>
              <li>✓ All processing happens in your browser (privacy-safe)</li>
            </ul>
          </div>
        </div>
      </div>

    </>
  );
};

export default CssBeautifierPage;
